import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { UpgradeGate } from "../components/Bits";
import { ShareHeatCard } from "../components/ShareHeatCard";
import { ZONE_COLS } from "./BatterZone";
import { Share2, Lock } from "lucide-react";

const HEAT = {
  great: "bg-emerald-600/50 text-emerald-100",
  ok: "bg-yellow-600/35 text-yellow-100",
  work: "bg-red-700/40 text-red-200",
};

function Cell({ grade, value, locked }) {
  const cls = HEAT[grade] || HEAT.work;
  if (locked) {
    return (
      <td className={`p-1.5 text-center font-bold ${cls}`}>
        <span className="blur-[4px] select-none opacity-80" aria-hidden="true">9.9</span>
      </td>
    );
  }
  return <td className={`p-1.5 text-center font-bold ${cls}`}>{value ?? "—"}</td>;
}

function LineupGrid({ side, locked, sortKey, sortDir, onSort }) {
  const GRADE_ORDER = { smash: 7, good: 6, "a+": 5, a: 4, b: 3, c: 2, d: 1, fade: 0, avoid: 0 };
  const num = (v) => {
    const s = String(v ?? "").trim().toLowerCase();
    if (GRADE_ORDER[s] != null) return GRADE_ORDER[s];
    const n = parseFloat(s.replace(/[^0-9.-]/g, ""));
    return isNaN(n) ? -Infinity : n;
  };
  const val = (r) => (sortKey === "hr_prob" ? r.hr_prob : sortKey === "adj_hr" ? (r.adj_hr ?? r.confidence) : r.stats?.[sortKey]?.value);
  const rows = [...(side.rows || [])].sort((a, b) => (sortDir === "desc" ? num(val(b)) - num(val(a)) : num(val(a)) - num(val(b))));
  const arrow = (k) => (sortKey === k ? (sortDir === "desc" ? " ▼" : " ▲") : "");
  const Th = ({ k, label, cls = "" }) => (
    <th className={`p-2 cursor-pointer select-none transition-colors duration-150 hover:text-[#a855f7] ${sortKey === k ? "text-[#a855f7]" : ""} ${cls}`}
      onClick={() => onSort(k)} data-testid={`heat-sort-${k}`} title="Click to sort best ↔ worst">
      {label}{arrow(k)}
    </th>
  );
  return (
    <div className="card-surface rounded-lg overflow-hidden" data-testid={`heat-side-${side.team.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
      <div className="bg-[#8b1526] border-b border-[#c22040]/40 px-3 py-2 flex flex-wrap items-center gap-2">
        <span className="text-xs font-black uppercase tracking-widest text-white">{side.team}</span>
        <span className="text-[10px] text-red-100/80">vs {side.pitcher?.name} ({side.pitcher?.hand}HP{side.pitcher?.vulnerability != null ? ` · vuln ${side.pitcher.vulnerability}/10` : ""})</span>
        <span className={`ml-auto text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${side.lineup_status === "confirmed" ? "bg-[#22c55e]/20 text-[#22c55e]" : "bg-white/10 text-neutral-300"}`}>
          {side.lineup_status}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[980px]">
          <thead>
            <tr className="text-neutral-500 text-[9px] uppercase tracking-wider border-b border-white/10">
              <th className="p-2 text-left sticky left-0 bg-[#141414] z-10">Hitter</th>
              <Th k="hr_prob" label="HR%" />
              <Th k="adj_hr" label="Adj HR%" cls={sortKey === "adj_hr" ? "" : "text-[#a855f7]/70"} />
              {ZONE_COLS.map(([k, label]) => <Th key={k} k={k} label={label} />)}
            </tr>
          </thead>
          <tbody className="font-data">
            {rows.map((r) => (
              <tr key={r.mlb_id} className="border-b border-white/5" data-testid={`heat-row-${r.mlb_id}`}>
                <td className="p-1.5 sticky left-0 bg-[#141414] z-10">
                  <Link to={`/research/${r.mlb_id}`} className="flex items-center gap-2 font-sans font-semibold hover:text-[#a855f7] transition-colors duration-200 whitespace-nowrap">
                    <img src={r.headshot} alt="" className="w-6 h-6 rounded-full object-cover bg-white/5" loading="lazy" />
                    {r.name}
                    {locked && <span className="text-[8px] uppercase tracking-widest font-black text-[#a855f7] bg-[#a855f7]/10 border border-[#a855f7]/40 px-1.5 py-0.5 rounded-full">Top bat</span>}
                  </Link>
                </td>
                {locked ? (
                  <>
                    <Cell grade="great" locked />
                    <Cell grade="great" locked />
                  </>
                ) : (
                  <>
                    <td className={`p-1.5 text-center font-bold ${r.hr_prob >= 8 ? HEAT.great : r.hr_prob >= 5 ? HEAT.ok : HEAT.work}`}>{r.hr_prob ?? "—"}</td>
                    <td className={`p-1.5 text-center font-black ring-1 ring-inset ring-[#a855f7]/30 ${r.adj_hr >= 9 ? HEAT.great : r.adj_hr >= 6 ? HEAT.ok : HEAT.work}`} data-testid={`adj-hr-${r.mlb_id}`}>{r.adj_hr ?? "—"}</td>
                  </>
                )}
                {ZONE_COLS.map(([k]) => (
                  <Cell key={k} grade={r.stats?.[k]?.grade} value={r.stats?.[k]?.value} locked={locked} />
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={17} className="p-3 text-neutral-600 font-sans">Lineup not available yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {locked && side.hidden_count > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-[#a855f7]/20 bg-[#a855f7]/5 text-[11px] text-[#a855f7] font-bold uppercase tracking-wider" data-testid={`heat-teaser-${side.team.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
          <Lock size={12} /> {side.hidden_count} more hitters in this lineup — Pro unlocks the full grid
        </div>
      )}
    </div>
  );
}

function ParkStrip({ park }) {
  if (!park) return null;
  const pct = park.boost_pct ?? 0;
  const badgeCls = pct >= 8 ? "bg-[#22c55e]/20 text-[#22c55e] border-[#22c55e]/60" : pct <= -5 ? "bg-sky-500/15 text-sky-300 border-sky-400/50" : "bg-white/10 text-neutral-300 border-white/20";
  return (
    <div className="card-surface rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="heat-park-strip">
      <span className="font-bold uppercase tracking-wide text-neutral-200">{park.venue}</span>
      <span className="text-neutral-400">HR Park <b className="text-[#a855f7]">{park.rating}/10</b></span>
      {park.roof === "dome" ? (
        <span className="text-neutral-400">Dome — weather neutral</span>
      ) : (
        <>
          {park.temp != null && <span className="text-neutral-400">{park.temp}°F</span>}
          {park.wind_speed != null && <span className="text-neutral-400">Wind {park.wind_speed} mph {park.wind_dir} <i className="not-italic text-neutral-600">({park.wind_note})</i></span>}
        </>
      )}
      <span className={`ml-auto text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${badgeCls}`} data-testid="heat-park-boost">
        {pct > 0 ? `+${pct}%` : `${pct}%`} HR boost
      </span>
    </div>
  );
}

export default function HeatBoard() {
  const { data, isLoading } = useQuery({ queryKey: ["heatBoard"], queryFn: () => api.get("/heat-board").then((r) => r.data) });
  const games = data?.games || [];
  const locked = !!data?.locked;
  const [game, setGame] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [sortKey, setSortKey] = useState("adj_hr");
  const [sortDir, setSortDir] = useState("desc");
  const onSort = (k) => {
    if (k === sortKey) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(k); setSortDir("desc"); }
  };
  useEffect(() => {
    if (!game && games.length > 0) setGame(games[0].game);
  }, [games, game]);
  const sel = games.find((g) => g.game === game);
  const top5 = games
    .flatMap((g) => g.sides.flatMap((s) => s.rows.map((r) => ({ name: r.name, team: r.team, pitcher: s.pitcher?.name, hr_prob: r.hr_prob }))))
    .filter((h) => h.hr_prob != null)
    .sort((a, b) => b.hr_prob - a.hr_prob)
    .slice(0, 5);

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Heat <span className="gold-text">Board</span></h1>
          {!locked && top5.length > 0 && (
            <button onClick={() => setShowShare(true)} data-testid="heat-share-btn"
              className="inline-flex items-center gap-1.5 border border-[#a855f7]/60 bg-[#a855f7]/10 text-[#a855f7] hover:bg-[#a855f7]/20 text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full transition-colors duration-200">
              <Share2 size={13} /> Share Heat Card
            </button>
          )}
        </div>
        <p className="text-neutral-500 text-sm mt-1">Every lineup, color-graded. <span className="text-emerald-400 font-bold">Green = HR potential</span>, yellow = solid, <span className="text-red-400 font-bold">red = fade</span>. Click any column header (Form%, Zone, ISO, wOBA...) to rank best → worst; click again to flip.</p>
      </div>
      {showShare && <ShareHeatCard hitters={top5} date={data?.date} onClose={() => setShowShare(false)} />}

      <div className="flex gap-2 overflow-x-auto pb-1 fade-up-1" data-testid="heat-game-chips">
        {games.map((g) => (
          <button key={g.game} onClick={() => setGame(g.game)} data-testid={`heat-chip-${g.game.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
            className={`shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg border transition-colors duration-200 ${game === g.game ? "bg-[#8b1526] border-[#c22040] text-white" : "border-white/15 text-neutral-400 hover:border-[#c22040]/60 hover:text-white"}`}>
            {g.game}
          </button>
        ))}
      </div>

      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Heating up the board...</div>}
      {!isLoading && games.length === 0 && (
        <div className="card-surface rounded-xl p-10 text-center text-neutral-400" data-testid="heat-empty">No games with probable starters today.</div>
      )}

      {sel && (
        <div className="space-y-4 fade-up-1" data-testid="heat-detail">
          <ParkStrip park={sel.park} />
          {sel.sides.map((s) => <LineupGrid key={s.team} side={s} locked={locked} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />)}
        </div>
      )}
      {locked && <UpgradeGate feature={`The full Heat Board — every hitter's grades for all ${data.total} games tonight`} />}
      <p className="text-[11px] text-neutral-600">Grades blend official MLB stats with model estimates (xwOBA, SwS%, Zone%). HR% is the model's home run probability tonight; Adj HR% factors in the ballpark, temperature and wind (wind direction is an estimate).</p>
    </div>
  );
}
